import type { Coords, Lan, Region } from "./types";

export interface RegionDef {
  id: Region;
  /** Display label (Title-cased Swedish/English mix). */
  label: string;
  /** Län this region encompasses. Drives the subline in ListItem. */
  lan: readonly Lan[];
  /** Approximate centroid used for region map markers. */
  coords: Coords;
}

/**
 * Single source of truth for regions, ordering, display labels, and
 * län membership. See [docs/regions.md](../../docs/regions.md) for
 * rationale. Keep ordered by population / activity (Stockholm + South
 * lead, the thin regions trail).
 */
export const REGIONS: readonly RegionDef[] = [
  {
    id: "stockholm",
    label: "Stockholm",
    lan: ["Stockholm", "Uppsala", "Södermanland"],
    coords: { lat: 59.33, lon: 18.07 },
  },
  {
    id: "south-sweden",
    label: "South Sweden",
    lan: ["Skåne", "Blekinge"],
    coords: { lat: 55.95, lon: 13.55 },
  },
  {
    id: "west-sweden",
    label: "West Sweden",
    lan: ["Västra Götaland", "Halland"],
    coords: { lat: 57.70, lon: 12.00 },
  },
  {
    id: "east-sweden",
    label: "East Sweden",
    lan: ["Östergötland", "Jönköping", "Kronoberg", "Kalmar"],
    coords: { lat: 58.00, lon: 15.60 },
  },
  {
    id: "central-sweden",
    label: "Central Sweden",
    lan: ["Värmland", "Örebro", "Västmanland", "Dalarna"],
    coords: { lat: 59.80, lon: 15.30 },
  },
  {
    id: "gotland",
    label: "Gotland",
    lan: ["Gotland"],
    coords: { lat: 57.47, lon: 18.49 },
  },
  {
    id: "oland",
    label: "Öland",
    /* Öland is part of Kalmar län but treated as its own region; we
       leave lan empty so Kalmar doesn't appear under two regions. */
    lan: [],
    coords: { lat: 56.74, lon: 16.63 },
  },
  {
    id: "north-sweden",
    label: "North Sweden",
    lan: [
      "Gävleborg",
      "Västernorrland",
      "Jämtland",
      "Västerbotten",
      "Norrbotten",
    ],
    coords: { lat: 64.50, lon: 17.50 },
  },
] as const;

/** Lookup helper. Returns the region definition or undefined if id is unknown. */
export function regionById(id: Region): RegionDef | undefined {
  return REGIONS.find((r) => r.id === id);
}

/** Format a region's län list as a comma-separated subtitle. */
export function regionSubtitle(region: RegionDef): string {
  return region.lan.length > 0 ? region.lan.join(", ") : "";
}
